import React from 'react';
import { Languages, GraduationCap, Info, Quote, ArrowRight } from 'lucide-react';
import { team, brand } from '../../mock';

const Team = () => {
  return (
    <section id="team" className="py-20 lg:py-28 bg-[var(--brand-green)] text-[var(--brand-cream)] relative overflow-hidden">
      <div className="absolute inset-0 grain" />
      <div className="mx-auto max-w-7xl px-6 lg:px-10 relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 reveal">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.24em] text-[var(--brand-gold-2)] mb-4">Our Clinical Team</div>
            <h2 className="font-serif text-4xl lg:text-6xl tracking-tight leading-[1.04]">
              Meet the <em className="italic">minds</em> behind Elevate.
            </h2>
          </div>
          <p className="max-w-md opacity-80 text-lg">
            A multidisciplinary team — doctors, a dietitian, nurses and support staff — working together on every plan.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {team.map((m) => (
            <div
              key={m.id}
              className="reveal card-hover rounded-[24px] overflow-hidden border border-[rgba(247,243,236,0.15)] bg-[rgba(247,243,236,0.04)] flex flex-col"
            >
              {/* Photo card with cream backdrop */}
              <div className="relative aspect-[4/5] bg-[#F5F1E8] overflow-hidden">
                {/* Subtle floor shadow for depth */}
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/8 to-transparent pointer-events-none" />
                {/* Portrait */}
                <img
                  src={m.image}
                  alt={m.name}
                  className={`absolute inset-0 h-full w-full ${
                    m.hasBackground ? 'object-cover object-top' : 'object-contain object-bottom'
                  }`}
                  style={m.photoScale ? { transform: `scale(${m.photoScale})`, transformOrigin: m.photoOrigin || 'center bottom' } : undefined}
                />
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <div className="font-serif text-2xl leading-tight">{m.name}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-[var(--brand-gold-2)] mt-2">{m.role}</div>

                <div className="mt-4 space-y-2 text-[12.5px] opacity-85">
                  <div className="flex items-start gap-2">
                    <GraduationCap size={14} className="mt-0.5 shrink-0 text-[var(--brand-gold-2)]" />
                    <span>{m.creds}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Languages size={14} className="mt-0.5 shrink-0 text-[var(--brand-gold-2)]" />
                    <span>Speaks {m.languages}</span>
                  </div>
                </div>

                <p className="mt-4 text-sm opacity-85 leading-relaxed">{m.bio}</p>

                <div className="mt-5">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--brand-gold-2)] mb-2">Areas of interest</div>
                  <div className="flex flex-wrap gap-1.5">
                    {m.focus.map((f) => (
                      <span key={f} className="text-[11px] px-2.5 py-1 rounded-full bg-[rgba(247,243,236,0.08)] border border-[rgba(247,243,236,0.15)]">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>

                {m.quote && (
                  <div className="mt-5 pt-5 border-t border-[rgba(247,243,236,0.12)] flex items-start gap-3">
                    <Quote size={14} className="mt-1 shrink-0 text-[var(--brand-gold-2)]" />
                    <p className="font-serif italic text-[15px] leading-snug opacity-95">{m.quote}</p>
                  </div>
                )}

                {m.note && (
                  <div className="mt-4 flex items-start gap-2 text-[12px] opacity-75 border-t border-[rgba(247,243,236,0.12)] pt-4">
                    <Info size={12} className="mt-0.5 shrink-0 text-[var(--brand-gold-2)]" />
                    <span>{m.note}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* CTA banner: Expertise You Can Trust */}
        <div className="reveal mt-14 rounded-[28px] p-8 lg:p-12 bg-[rgba(247,243,236,0.06)] border border-[rgba(247,243,236,0.15)] grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="text-xs uppercase tracking-[0.24em] text-[var(--brand-gold-2)] mb-3">Care that's truly collaborative</div>
            <h3 className="font-serif text-3xl lg:text-4xl leading-[1.1]">
              Expertise you can <em className="italic">trust</em>. Care that's truly collaborative.
            </h3>
            <p className="mt-4 opacity-85 max-w-xl">
              A multidisciplinary team of practitioners and nurses working collaboratively to deliver personalised, preventative care — with expertise across functional medicine, hormonal health and chronic disease prevention.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <a href={brand.bookingUrl} target="_blank" rel="noopener noreferrer" className="btn-primary !bg-[var(--brand-gold)] !border-[var(--brand-gold)] !text-[var(--brand-green)] hover:!bg-[var(--brand-gold-2)] text-sm">
              Book a consultation <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Team;
